import { BUSINESS_ROLE_GROUPS } from '@/components/security/AppRoles';
import type { ROLES } from '@/components/security/AppRoles';
import { icons } from '@/assets';

export interface MenuItemConfig {
  id: string;
  label: string;
  route: string;
  icon: string;
  alt: string;
  permission?: ROLES | readonly ROLES[];
  imageClassName?: string;
  cardClassName?: string;
  labelClassName?: string;
}

export interface MenuSectionConfig {
  id: string;
  title: string;
  description: string;
  items: MenuItemConfig[];
}

export const businessMenuSections: MenuSectionConfig[] = [
  {
    id: 'shippings-routes',
    title: 'Envíos y rutas',
    description: 'Pedidos, retiros, rutas y monitoreo del día a día',
    items: [
      {
        id: 'shippings',
        label: 'Envíos',
        route: '/business/shippings',
        icon: icons.preparePackage,
        alt: 'Icono de pedidos',
        imageClassName: 'h-[90px] object-contain',
      },
      {
        id: 'monitoring',
        label: 'Gestión de Rutas',
        route: '/business/monitoring',
        icon: icons.routes,
        alt: 'Icono de gestión de rutas',
        permission: BUSINESS_ROLE_GROUPS.adminOrDispatcher,
        imageClassName: 'h-[70px] object-contain',
      },
      {
        id: 'pickups',
        label: 'Retiros Flex',
        route: '/business/pickup',
        icon: icons.vehicles,
        alt: 'Icono de retiros flex',
        permission: BUSINESS_ROLE_GROUPS.full,
        imageClassName: 'h-[90px] object-contain',
      },
      {
        id: 'meli-orders',
        label: 'Estados MELI',
        route: '/business/shippings/meli-orders',
        icon: icons.meli,
        alt: 'Icono de estados MELI',
        imageClassName: 'h-[120px] mt-[-30px] object-contain',
      },
      {
        id: 'transit-control',
        label: 'Control de Tránsito',
        route: '/business/transit-control',
        icon: icons.monitor,
        alt: 'Icono de control de tránsito',
        permission: BUSINESS_ROLE_GROUPS.operations,
        imageClassName: 'h-[80px] object-contain',
      },
      {
        id: 'driver-monitoring',
        label: 'Monitoreo de conductores',
        route: '/business/driver-monitoring',
        icon: icons.drivers,
        alt: 'Icono de monitoreo de conductores',
        permission: BUSINESS_ROLE_GROUPS.management,
        imageClassName: 'h-[70px] object-contain',
      },
    ],
  },
  {
    id: 'operation',
    title: 'Operación',
    description: 'Escaneo, cambios de estado y gestiones en depósito',
    items: [
      {
        id: 'logistics-network',
        label: 'Red logística',
        route: '/business/logistics-network',
        icon: icons.geography,
        alt: 'Icono de red logística',
        imageClassName: 'h-[80px] object-contain',
      },
      {
        id: 'arrival-scanning',
        label: 'Escanear Arribos',
        route: '/business/arrival-scanning',
        icon: icons.scanGun,
        alt: 'Icono de escanear arribos',
        permission: BUSINESS_ROLE_GROUPS.operations,
        imageClassName: 'h-[120px] mt-[-6px] mb-[-24px] ml-[12px] object-contain',
        cardClassName: 'py-[10px] px-[25px]',
        labelClassName: 'text-center mb-1',
      },
      {
        id: 'batch-checkpoints',
        label: 'Cambio de Estado Múltiple',
        route: '/business/batch-checkpoints',
        icon: icons.statusChange,
        alt: 'Icono de cambio de estado múltiple',
        permission: BUSINESS_ROLE_GROUPS.operations,
        imageClassName: 'h-[125px] mt-[-18px] mb-[-15px] ml-[-12px] object-contain',
        cardClassName: 'py-[10px] px-[25px]',
        labelClassName: 'text-center mb-3',
      },
      {
        id: 'returns-intake',
        label: 'Rendiciones',
        route: '/business/returns-intake',
        icon: icons.rendiciones,
        alt: 'Icono de rendiciones',
        permission: BUSINESS_ROLE_GROUPS.operations,
        imageClassName: 'h-[80px] object-contain',
      },
      {
        id: 'amendments',
        label: 'Enmiendas',
        route: '/business/amendments',
        icon: icons.invoice,
        alt: 'Icono de enmiendas',
        permission: BUSINESS_ROLE_GROUPS.operations,
        imageClassName: 'h-[80px] object-contain',
      },
      {
        id: 'authorizations',
        label: 'Autorizaciones',
        route: '/business/authorizations',
        icon: icons.tasks,
        alt: 'Icono de autorizaciones',
        permission: BUSINESS_ROLE_GROUPS.withoutDispatcher,
        imageClassName: 'h-[80px] object-contain',
      },
      {
        id: 'jobs',
        label: 'Mis archivos',
        route: '/business/jobs',
        icon: icons.graphics,
        alt: 'Icono de mis archivos',
        permission: BUSINESS_ROLE_GROUPS.full,
        imageClassName: 'h-[80px] object-contain',
      },
      {
        id: 'billing-reports',
        label: 'Facturación',
        route: '/business/billing-reports',
        icon: icons.cost,
        alt: 'Icono de facturación',
        permission: BUSINESS_ROLE_GROUPS.billing,
        imageClassName: 'h-[80px] object-contain',
      },
    ],
  },
];

export const logisticsNetworkMenuItems: MenuItemConfig[] = [
  {
    id: 'users',
    label: 'Usuarios',
    route: '/business/users',
    icon: icons.users,
    alt: 'Icono de usuarios',
    permission: BUSINESS_ROLE_GROUPS.withoutDispatcher,
    imageClassName: 'h-[80px] object-contain',
  },
  {
    id: 'drivers',
    label: 'Conductores',
    route: '/business/drivers',
    icon: icons.drivers,
    alt: 'Icono de conductores',
    imageClassName: 'h-[70px] object-contain',
  },
  {
    id: 'commerce',
    label: 'Comercios',
    route: '/business/commerce',
    icon: icons.commerces,
    alt: 'Icono de comercios',
    permission: BUSINESS_ROLE_GROUPS.withoutDispatcher,
    imageClassName: 'h-[70px] object-contain',
  },
  {
    id: 'logistic-operator',
    label: 'Operadores logísticos',
    route: '/business/logistic-operator',
    icon: icons.logisticOperators,
    alt: 'Icono de operadores logísticos',
    imageClassName: 'h-[70px] object-contain',
  },
  {
    id: 'service-visit-types',
    label: 'Tipos de visita',
    route: '/business/service-visit-types',
    icon: icons.settingsCatalogs,
    alt: 'Icono de tipos de visita',
    permission: BUSINESS_ROLE_GROUPS.management,
    imageClassName: 'h-[70px] object-contain',
  },
  {
    id: 'locations',
    label: 'Auditoría Pickup',
    route: '/business/shippings/pickup-audit',
    icon: icons.location,
    alt: 'Icono de auditoría pickup',
    permission: BUSINESS_ROLE_GROUPS.operations,
    imageClassName: 'mt-[-20px] h-[100px] object-contain',
  },
];
